import { toolTuningFields } from '../ccm'
import { regexEscape } from '../escape-utils'
import { SupportedTools } from '../interfaces'

import { Tool } from './base'
import {
  makeRenderer,
  PipeAndGroups,
  RenderContext,
  normalizeList,
  scalarToString,
  alt,
  altAnchored,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithPipeBlock,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { logscaleMap } from './render/rtd/tool-datasource-mappings/crowdstrike-logscale.datasource'
import { AstPredicate } from './render/types'

/** Map normalized platform values -> LogScale event_platform values.
 *  Cloud providers map to '' so they are silently dropped by .filter(Boolean) —
 *  their routing is handled via #event.module / #event.dataset instead. */
const platformValueMap: Record<string, string> = {
  windows: 'Win',
  linux: 'Lin',
  mac: 'Mac',
  azure: '',
  aws: '',
  gcp: '',
  cloud: '',
}

export const logscaleDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| fields ${fields.join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    // Accounting for default time stamp handling across all categories
    // count(aid) is how CS Engineers do a general "event count"
    // firstSeen/lastSeen are universal across all LogScale categories
    // These are always going to be added to custom AST aggClauses, this behavior should not change
    // While hardcoded is not ideal, it simplifies the mental model for customers since they don't need to know about special reserved field names or add them manually to their AST — we can just document that aid is used for count and TargetProcessId is used for min/max timestamps in process_creation events.
    const allClauses = [
      'count(aid, as=executeCount)',
      'min(@timestamp, as=firstSeen)',
      'max(@timestamp, as=lastSeen)',
      ...aggClauses,
    ]
    return [
      `| groupBy([${groupFields.join(', ')}], function=[${allClauses.join(', ')}])`,
      // Because the timestamps are now hardcoded above, we can add the formatting here
      '| firstSeen:=formattime(field=firstSeen, format="%Y/%m/%d %H:%M:%S")',
      '| lastSeen:=formattime(field=lastSeen, format="%Y/%m/%d %H:%M:%S")',
    ].join('\n')
  },

  timeBucket: (span, _groupFields, aggClauses) => {
    const fn = aggClauses[0] ?? 'count()'
    return `| bucket(span=${span}, function=${fn})`
  },

  aggFns: {
    count: (field, alias) =>
      field ? `count(${field}, as=${alias})` : `count(as=${alias})`,
    dcount: (field, alias) => `count(${field}, as=${alias}, distinct=true)`,
    collect: (field, alias) => `collect([${field}], as=${alias})`,
    values: (field, alias) => `collect([${field}], as=${alias})`,
    first: (field, alias) => `selectFirst([${field}], as=${alias})`,
    last: (field, alias) => `selectLast([${field}], as=${alias})`,
    sum: (field, alias) => `sum(${field}, as=${alias})`,
    avg: (field, alias) => `avg(${field}, as=${alias})`,
    min: (field, alias) => `min(${field}, as=${alias})`,
    max: (field, alias) => `max(${field}, as=${alias})`,
  },

  fieldAlias: null, // LogScale renames via `as=` within function calls, not standalone
  // Failsafe in Crowdstrike LogScale to prevent unbounded collect() calls from generating OOM errors
  // Will require manual adjustment after emitting the query if customers want to expand the total results
  mergeCollect: { limit: 1000 },

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { logscaleMap }

export class CrowdStrikeLogScaleTool extends Tool {
  public readonly toolName = 'CrowdStrike LogScale'
  public readonly toolKind = 'xdr' as const
  public override readonly datasourceMap = logscaleMap
  public readonly dataDisplayTemplate = logscaleDataDisplayTemplate

  // Customers commonly save their rootUrl as .../investigate/search (copied from the browser)
  // rather than the expected .../investigate. We can't force a change without breaking existing
  // configs, so we strip the trailing /search here to support both variants.
  public override normalizeRootUrl(url: string): string {
    return super.normalizeRootUrl(url).replace(/\/search$/, '')
  }

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${this.normalizeRootUrl(rootURL)}/search?live=false&query=${encodedRenderedContent}&start=1d`
  }

  public pivotEscape(key: string, value: string): string {
    return regexEscape(value)
  }

  /** Notes and Implementation
   * Fields
   * - Map via toolTuningFields["crowdstrike_logscale"]; **no Eta** wrapping.
   *
   * Values & Lists
   * - Non-regex values always **double-quoted**; regex bodies unquoted.
   * - Regex is case-insensitive: `/.../i`.
   *
   * Layout, Logic & Grouping
   * - Uses PipeAndGroups:
   *   • Any **AND** group → one line per child, each prefixed with `| `.
   *   • **OR** groups → single parenthesized line: `(a OR b)`.
   * - Logical glue tokens: ` AND ` / ` OR `.
   *
   * Operators
   * - contains / doesnotcontain: `F = /v/i` / `F != /v/i`.
   * - startswith / endswith: `F = /^v/i` / `F = /v$/i`.
   * - Regex multi-values collapse to alternation: `/v1|v2|v3/i` (anchored per op).
   * - eq / neq: single `F = "v"` / `F != "v"`; multi `(F = "v1" OR F = "v2")` / `(F != "v1" AND F != "v2")`.
   * - Comparisons: `F < "v"`, `<=`, `>`, `>=` (quoted).
   *
   * Null / Empty
   * - Defaults: `F = null`, `F != null`, `F = ""`, `F != ""` (adjust if your env uses exists() semantics).
   *
   * Safety
   * - Sanitize identifiers; escape regex with a safe helper; use centralized quoting for non-regex.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: PipeAndGroups,
    renderContext: {
      linePrefix: '| ',
      andGlue: ' AND ',
      orGlue: ' OR ',
      regexFlags: 'i',
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const f = renderContext.mapField(p.field)
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      const Q = (s: string) => renderContext.quote(s)
      const rx = (body: string) => `/${body}/${renderContext.regexFlags}`

      switch (p.op) {
        // Equality / inequality (double-quoted)
        case 'eq':
          return isMulti
            ? `(${vals.map((v) => `${f} = ${Q(v)}`).join(renderContext.orGlue)})`
            : `${f} = ${Q(vals[0])}`
        case 'neq':
          return isMulti
            ? `(${vals.map((v) => `${f} != ${Q(v)}`).join(renderContext.andGlue)})`
            : `${f} != ${Q(vals[0])}`

        // Regex operators: collapse multi to alternation
        case 'contains':
          return `${f} = ${rx(alt(vals))}`
        case 'doesnotcontain':
          return `${f} != ${rx(alt(vals))}`
        case 'startswith':
          return `${f} = ${rx(altAnchored(vals, '^'))}`
        case 'endswith':
          return `${f} = ${rx(altAnchored(vals, '$'))}`

        // Comparisons (quoted per rule)
        case 'lt':
          return `${f} < ${Q(vals[0])}`
        case 'lte':
          return `${f} <= ${Q(vals[0])}`
        case 'gt':
          return `${f} > ${Q(vals[0])}`
        case 'gte':
          return `${f} >= ${Q(vals[0])}`

        // Null / empty
        case 'isnull':
          return `${f} != *`
        case 'isnotnull':
          return `${f} = *`
        case 'isempty':
          return `${f} = ""`
        case 'isnotempty':
          return `${f} != ""`

        default:
          return isMulti
            ? `(${vals.map((v) => `${f} = ${Q(v)}`).join(renderContext.orGlue)})`
            : `${f} = ${Q(vals[0])}`
      }
    },
  })

  public readonly tuningText =
    'Insert tuning logic after the last line of base logic (ex. | CommandLine = /example/i). This will typically be right before the \'| default(field=GrandParentBaseFileName, value="Unknown")\' function.'

  /** Emit the LogScale datasource prelude from a resolved NormalizedDataSource */
  public emitLogScaleDatasource(
    ds: NormalizedDataSource | undefined,
    tool: SupportedTools,
  ): string {
    if (!ds) return ''
    const lines: string[] = []
    const Q = (s: string) => JSON.stringify(String(s))

    // Routing metadata fields — resolved from fieldMap; absent entries are silently skipped
    const repoField = toolTuningFields[tool]?.['repo']
    const repoSubtypeField = toolTuningFields[tool]?.['repo_subtype']
    const eventProviderField = toolTuningFields[tool]?.['event_provider']

    if (repoField && ds.repo) lines.push(`${repoField}="${ds.repo}"`)

    // repoSubtype / eventProvider: emit multi-value IN(...) for multi-cloud; single-value for normal
    if (repoSubtypeField) {
      const vals = ds.repoSubtypes ?? (ds.repoSubtype ? [ds.repoSubtype] : [])
      if (vals.length === 1) lines.push(`${repoSubtypeField}="${vals[0]}"`)
      else if (vals.length > 1)
        lines.push(
          `in(field=${repoSubtypeField}, values=[${vals.map(Q).join(',')}])`,
        )
    }
    if (eventProviderField) {
      const vals =
        ds.eventProviders ?? (ds.eventProvider ? [ds.eventProvider] : [])
      if (vals.length === 1) lines.push(`${eventProviderField}="${vals[0]}"`)
      else if (vals.length > 1)
        lines.push(
          `in(field=${eventProviderField}, values=[${vals.map(Q).join(',')}])`,
        )
    }

    // Resolve events field name from the canonical 'event_type' mapping
    const eventsField =
      toolTuningFields[tool]?.['event_type'] ?? '#event_simpleName'

    if (ds.events?.length) {
      const quotedEvents = ds.events.map(Q).join(',')
      lines.push(
        ds.events.length === 1
          ? `${eventsField} = ${Q(ds.events[0])}`
          : `in(field=${eventsField}, values=[${quotedEvents}])`,
      )
    }

    // Platform clause field: prefer map-resolved extra, fall back to fieldMap lookup, then hard default.
    const platFallback =
      toolTuningFields[tool]?.['event_platform'] ?? 'event_platform'
    const platField =
      (ds.extra?.platformClauseList as string[] | undefined)?.[0] ??
      (ds.extra?.['platformClause'] as string | undefined) ??
      platFallback

    const platValues = this.resolvePlatformValues(ds.platform)
    if (platValues.length) {
      lines.push(
        platValues.length === 1
          ? `${platField}=${platValues[0]}`
          : `in(field=${platField}, values=[${platValues.join(',')}])`,
      )
    }

    return lines.length ? lines.join('\n| ') : ''
  }

  public resolvePlatformValues(platform?: string | string[]): string[] {
    if (!platform) return []
    const ps = Array.isArray(platform) ? platform : [platform]
    // Known platforms are normalized; unknown values pass through as-is -- could introduce bad matches
    // callers can supply custom values like "cloud" without needing a map entry.
    return ps.map((p) => platformValueMap[p.toLowerCase()] ?? p).filter(Boolean)
  }

  /** Merge the base logic (tuning) with expanded logic for datasources, dataDisplay and other directives */
  public renderCrowdstrikeLogScale = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitLogScaleDatasource(ds, this.toolName),
      joinWithPipeBlock,
      this.tuningText,
    ),
    this.toolName,
    logscaleDataDisplayTemplate,
  )
}
